import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { X, Mail, Phone, User, CheckCircle2, Loader2 } from "lucide-react";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const LeadCaptureModal = ({ open, onClose, scenario }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const overlayRef = useRef(null);
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, ease: "power2.out" }
      );
      gsap.fromTo(
        sheetRef.current,
        { y: 60, opacity: 0, scale: 0.97 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.55,
          ease: "power3.out",
          delay: 0.05,
        }
      );
    });
    return () => ctx.revert();
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!phone.trim()) {
      setError("Phone is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/leads`, {
        method: "POST",
        headers: {
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${SUPABASE_KEY}`,
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          scenario,
        }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to submit");
      }
      setSuccess(true);
    } catch (err) {
      const detail = err?.message || "Something went wrong. Try again.";
      setError(typeof detail === "string" ? detail : "Please check your inputs");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      data-testid="lead-capture-modal"
      className="fixed inset-0 z-[70] flex items-end md:items-center justify-center bg-black/40 dark:bg-black/70 backdrop-blur-sm px-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.("dismissed");
      }}
    >
      <div
        ref={sheetRef}
        className="relative w-full max-w-md bg-white border-zinc-200 dark:bg-zinc-900 dark:border-white/10 border rounded-3xl p-6 md:p-8 shadow-[0_20px_80px_rgba(0,0,0,0.25)] dark:shadow-[0_20px_80px_rgba(0,0,0,0.6)]"
      >
        {/* Close */}
        <button
          type="button"
          onClick={() => onClose?.("dismissed")}
          aria-label="Close"
          data-testid="lead-modal-close"
          className="absolute top-4 right-4 h-9 w-9 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 dark:text-zinc-500 dark:hover:text-zinc-200 dark:hover:bg-zinc-800 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        {success ? (
          <div className="py-6 text-center" data-testid="lead-success-state">
            <div className="mx-auto h-14 w-14 rounded-full bg-brand/15 text-brand dark:text-brand flex items-center justify-center mb-4">
              <CheckCircle2 className="h-7 w-7" strokeWidth={2} />
            </div>
            <h3 className="font-display text-2xl font-medium text-zinc-900 dark:text-zinc-50 tracking-tight mb-2">
              Thank you
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
              Thanks for submitting the information. CapitalCanvas Team will get
              back to you after BYC3! Enjoy your event today.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs tracking-[0.2em] uppercase text-brand dark:text-brand font-semibold mb-3">
              Optional
            </p>
            <h3 className="font-display text-2xl md:text-3xl font-medium text-zinc-900 dark:text-zinc-50 tracking-tight leading-tight mb-2">
              Ready to achieve?
              <br />
              <span className="text-zinc-400 dark:text-zinc-500">Let CapitalCanvas Team help you</span>
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6 leading-relaxed">
              Drop your details to receive a call back to plan your goal achieving blueprint, personalised for you — including SIP, lumpsum and other recommendations.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <Field
                icon={User}
                placeholder="Your name"
                value={name}
                onChange={setName}
                testId="lead-name-input"
                required
              />
              <Field
                icon={Mail}
                placeholder="Email address"
                type="email"
                value={email}
                onChange={setEmail}
                testId="lead-email-input"
                required
              />
              <Field
                icon={Phone}
                placeholder="Phone"
                type="tel"
                value={phone}
                onChange={setPhone}
                testId="lead-phone-input"
                required
              />

              {error && (
                <p
                  data-testid="lead-error"
                  className="text-xs text-red-400 mt-1"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-submit-button"
                className="mt-2 w-full py-3.5 rounded-full bg-zinc-900 text-zinc-50 hover:bg-zinc-950 dark:bg-zinc-50 dark:text-zinc-950 dark:hover:bg-white font-semibold text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Scheduling
                  </>
                ) : (
                  "Request a callback"
                )}
              </button>

              <button
                type="button"
                onClick={() => onClose?.("skipped")}
                data-testid="lead-skip-button"
                className="mt-1 w-full py-2.5 text-sm text-zinc-500 hover:text-zinc-900 dark:text-zinc-500 dark:hover:text-zinc-300 transition-colors"
              >
                Skip for now
              </button>
            </form>

            <p className="text-[11px] text-zinc-500 dark:text-zinc-600 text-center mt-4 leading-relaxed">
              We&apos;ll never spam or share your data. Pinky promise
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, value, onChange, testId, ...rest }) => (
  <label className="relative flex items-center">
    <Icon
      className="absolute left-4 h-4 w-4 text-zinc-500 dark:text-zinc-500 pointer-events-none"
      strokeWidth={2}
    />
    <input
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-zinc-50 border-zinc-200 text-zinc-900 placeholder:text-zinc-400 focus:bg-white focus:border-zinc-400 dark:bg-zinc-950 dark:border-white/10 dark:text-zinc-50 dark:placeholder:text-zinc-600 dark:focus:border-white/30 dark:focus:bg-zinc-900 border text-sm focus:outline-none transition-colors"
      {...rest}
    />
  </label>
);
